import React, { FC, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { Trans } from "react-i18next";
import { Action } from "../../../lmsApi/action/action";
import { ActionService } from '../../../lmsApi/action/service';
import TableComponent from "../../../component/table/tableComponent";
import { ErrorHandler } from "../../../service/errorHandler";
import { TableLoadData, HeaderItem, TableRowAction, TableGroupAction } from "../../../component/table/tableComponent/types";
import Utils from "../../../utils";
import { ActionParams } from "../../../lmsApi/action/types";

interface IProps {
    filterValues?: { [key: string]: any }
}

const ActionTable: FC<IProps> = (props) => {
    const history = useHistory();

    const header = useMemo<HeaderItem[]>(() => [
        {
            textId: 'main:field-id',
            field: 'id',
            sortable: true,
            getCellObject: (item: Action) => {
                return (<td className="align-middle">{item.id}</td>);
            }
        },
        {
            textId: 'action:name',
            field: 'name',
            alwaysVisible: true,
            getCellObject: (item: Action) => {
                return (
                    <td className="align-middle">
                        <Link className="link-hover" to={ActionService.getDetailLink(item)}>{item.name}</Link>
                    </td>
                );
            }
        },
        {
            textId: 'main:field-type',
            field: 'type',
            alwaysVisible: true,
            getCellObject: (item: Action) => {
                return (
                    <td className="align-middle">
                        <Trans>action:type-{item.type}</Trans>
                    </td>
                );
            }
        },
        {
            textId: 'action:data-name-webhook-link',
            field: 'webhook',
            getCellObject: (item: Action) => {
                const data = Utils.tryParseJson<{ [key: string]: string }>(item.data ?? '');
                return (
                    <td className="align-middle">{data ? data[ActionParams.webhookLink] : ''}</td>
                );
            }
        }
    ], []);

    const rowActions = useMemo<TableRowAction[]>(() => [
        {
            textId: 'main:btn-copy',
            action: (e: React.SyntheticEvent, item: Action) => clickNavigate(e, ActionService.getCopyLink(item)),
            refreshTableAfterAction: false
        },
        {
            textId: 'main:btn-delete',
            action: (e: React.SyntheticEvent, item: Action) => clickDelete(e, [item.id])
        }
    ], []);

    const groupActions = useMemo<TableGroupAction[]>(() => [
        {
            id: 'delete',
            textId: 'main:btn-delete',
            action: (e: React.SyntheticEvent, selected: string[]) => clickDelete(e, selected.map(x => Number(x)))
        }
    ], []);

    const loadData = async (data: TableLoadData) => {
        var requestData = {
            order: { [data.sortBy]: data.sortOrder.toUpperCase() },
            filter: { ...data.filterValues },
            skip: data.skip,
            take: data.take
        };
        const result = await ActionService.list(requestData);
        return result;
    }

    const clickDelete = async (e: React.SyntheticEvent, ids: number[]) => {
        try {
            if (e) e.preventDefault();
            await ActionService.delete(ids);
        }
        catch (err) {
            ErrorHandler.handle('delete actions', err);
        }
    }

    const clickNavigate = async (e: React.SyntheticEvent, link: string) => {
        if (e) e.preventDefault();
        history.push(link);
    }

    return (
        <TableComponent id="action-table-component" filterValues={props.filterValues} defaultVisibleColumns={['id', 'name']} header={header}
            loadData={loadData} rowActions={rowActions} groupActions={groupActions} />
    );
}

export default ActionTable;
